import React, { useState, useEffect, useRef } from 'react';
import { employeesApi } from '../services/api';
import { useEmployee } from '../contexts/EmployeeContext';

interface WorkstationLockScreenProps {
  onUnlock?: () => void;
}

export const WorkstationLockScreen: React.FC<WorkstationLockScreenProps> = ({ onUnlock }) => {
  const { employee, setEmployee, isLocked, isLoggedIn, employeeModeEnabled, employeeModeLoading } = useEmployee();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Focus input when component mounts
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handlePinInput = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 6) {
      setPin(digitsOnly);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await employeesApi.authenticate(pin);

      // Success - either same employee or different employee
      const newEmployee = data.employee;
      
      console.log('[Auth] Login successful:', {
        newEmployee: newEmployee.first_name + ' ' + newEmployee.last_name,
        role: newEmployee.role,
        permissions: newEmployee.permissions,
        previousEmployee: employee ? employee.first_name + ' ' + employee.last_name : 'none'
      });

      if (employee && newEmployee.id !== employee.id) {
        // Different employee is logging in - switch employees
        console.log(`[Employee Switch] ${employee.first_name} → ${newEmployee.first_name}`);
      }

      // setEmployee handles unlocking and resetting timers internally
      // No need to call unlock() separately
      setEmployee(newEmployee);
      setPin(''); // Clear PIN for security

      if (onUnlock) {
        onUnlock();
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Check for specific error types
      if (error.response?.status === 401) {
        // Could be Clerk session expired or invalid PIN
        if (error.response?.data?.error === 'Invalid PIN') {
          setError('Invalid PIN. Please try again.');
        } else {
          // Clerk session issue
          setError('Session expired. Please refresh the page.');
        }
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again.');
      } else if (!error.response) {
        setError('Network error. Check your connection.');
      } else {
        setError('Invalid PIN. Please try again.');
      }
      
      setPin('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleNumberClick = (num: string) => {
    if (pin.length < 6) {
      handlePinInput(pin + num);
    }
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  // Don't show anything while loading the employee mode setting
  if (employeeModeLoading) {
    return null;
  }

  // Only show lock screen if employee mode is enabled
  // If disabled, users can access the app without PIN login
  if (!employeeModeEnabled) {
    return null;
  }

  // Show if not logged in OR if locked
  const shouldShow = !isLoggedIn || isLocked;
  
  if (!shouldShow) {
    return null;
  }

  // Check localStorage for last active employee (for display purposes only)
  let lastActiveEmployee: { first_name: string; last_name: string } | null = null;
  try {
    const stored = localStorage.getItem('logged_in_employee');
    if (stored) {
      lastActiveEmployee = JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }

  const showMessage = isLocked || lastActiveEmployee ? 'Workstation Locked' : 'Employee Login Required';
  const subMessage = lastActiveEmployee
    ? `Last active: ${lastActiveEmployee.first_name} ${lastActiveEmployee.last_name}`
    : 'Enter your PIN to continue';

  return (
    <div
      data-prevent-scanner-focus="true"
      className="fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center"
    >
      <div className="w-full max-w-md px-6">
        {/* Header with time */}
        <div className="text-center mb-8">
          <div className="text-6xl font-light text-white mb-4">
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-lg text-slate-400">
            {currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo/Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-brand-blue rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
            {showMessage}
          </h2>
          <p className="text-sm text-slate-600 text-center mb-6">
            {subMessage}
          </p>

          {/* PIN Form */}
          <form onSubmit={handleSubmit}>
            {/* PIN Display */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2 text-center">
                Enter PIN
              </label>
              <input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => handlePinInput(e.target.value)}
                className="w-full px-4 py-4 text-3xl text-center font-mono border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent tracking-widest"
                placeholder="••••"
                maxLength={6}
                autoFocus
              />
              {error && (
                <div className="mt-2 text-center">
                  <p className="text-sm text-red-600">{error}</p>
                  {error.includes('refresh') && (
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="mt-2 text-sm text-brand-blue hover:underline"
                    >
                      Click here to refresh
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Number Pad */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleNumberClick(num.toString())}
                  className="h-16 text-2xl font-semibold bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl transition-colors"
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={handleClear}
                className="h-16 text-base font-medium bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => handleNumberClick('0')}
                className="h-16 text-2xl font-semibold bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl transition-colors"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleBackspace}
                className="h-16 text-xl font-medium bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl transition-colors"
              >
                ←
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={pin.length < 4 || loading}
              className="w-full px-6 py-4 text-lg font-medium text-white bg-brand-blue hover:bg-brand-blue-hover active:bg-brand-blue-hover rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? 'Authenticating...' : 'Unlock Workstation'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">
              CraftyShip Workstation • Locked after 10 min of inactivity
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
